import React from 'react';
import { ArrowRight, FileText, Users, Clock, CheckCircle, Plus, Briefcase, Calendar, TrendingUp } from 'lucide-react';

export default function ClientDashboard({ user, requirements, setActive }) {
  const live = (requirements || []).filter(r => r.status === 'live').length;
  const pending = (requirements || []).filter(r => r.status === 'pending').length;
  const closed = (requirements || []).filter(r => r.status === 'closed').length;
  const total = (requirements || []).length;

  const stats = [
    { label: 'Total Requirements', value: total, icon: FileText, change: 'All time', color: '#6366f1' },
    { label: 'Live Jobs', value: live, icon: CheckCircle, change: 'Currently active', color: '#16a34a' },
    { label: 'Under Review', value: pending, icon: Clock, change: 'Awaiting approval', color: '#f59e0b' },
    { label: 'Closed', value: closed, icon: Briefcase, change: 'Completed', color: '#6b7280' },
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", sans-serif' }}>

      {/* Page Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: '#0f172a', fontSize: '20px', fontWeight: '700', margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          Good {new Date().getHours() < 12 ? 'Morning' : 'Afternoon'}, {user.name.split(' ')[0]}
        </h1>
        <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
          {total === 0 ? 'Post your first requirement to start finding talent.' : `You have ${live} active job${live !== 1 ? 's' : ''} and ${pending} under review.`}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb', transition: 'all 0.15s', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
              onClick={() => setActive('requirements')}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} color={s.color} />
                </div>
              </div>
              <div style={{ color: '#0f172a', fontSize: '24px', fontWeight: '700', letterSpacing: '-0.5px', marginBottom: '3px' }}>{s.value}</div>
              <div style={{ color: '#0f172a', fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>{s.label}</div>
              <div style={{ color: '#9ca3af', fontSize: '11px' }}>{s.change}</div>
            </div>
          );
        })}
      </div>

      {/* Two column */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>

        {/* Quick Actions */}
        <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <h3 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600', margin: 0 }}>Quick Actions</h3>
          </div>
          <div style={{ padding: '12px' }}>
            {[
              { label: 'Post New Requirement', desc: 'Find the right IT talent', action: 'requirements', icon: Plus, color: '#6366f1' },
              { label: 'Browse Talent Pool', desc: 'Explore available candidates', action: 'bench', icon: Users, color: '#16a34a' },
              { label: 'View Candidates', desc: 'Review submitted profiles', action: 'candidates', icon: Briefcase, color: '#f59e0b' },
              { label: 'Schedule Interview', desc: 'Manage interview slots', action: 'interviews', icon: Calendar, color: '#8b5cf6' },
            ].map(a => {
              const Icon = a.icon;
              return (
                <div key={a.label} onClick={() => setActive(a.action)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 8px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '7px', background: `${a.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={15} color={a.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#111827', fontSize: '13px', fontWeight: '500' }}>{a.label}</div>
                    <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '1px' }}>{a.desc}</div>
                  </div>
                  <ArrowRight size={13} color="#d1d5db" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Requirements */}
        <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600', margin: 0 }}>Recent Requirements</h3>
            <button onClick={() => setActive('requirements')} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div style={{ padding: '8px 0' }}>
            {(requirements || []).slice(0, 5).map(r => (
              <div key={r._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => setActive('candidates')}>
                <div>
                  <div style={{ color: '#111827', fontSize: '13px', fontWeight: '500' }}>{r.title}</div>
                  <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>{r.experience} yrs · ₹{r.budget?.toLocaleString()}/mo</div>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                  background: r.status === 'live' ? '#f0fdf4' : r.status === 'pending' ? '#fffbeb' : '#f9fafb',
                  color: r.status === 'live' ? '#16a34a' : r.status === 'pending' ? '#ca8a04' : '#6b7280' }}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: r.status === 'live' ? '#16a34a' : r.status === 'pending' ? '#ca8a04' : '#9ca3af' }}/>
                  {r.status?.toUpperCase()}
                </span>
              </div>
            ))}
            {(requirements || []).length === 0 && (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 12px' }}>No requirements posted yet</p>
                <button onClick={() => setActive('requirements')} style={{ padding: '7px 16px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' }}>
                  Post First Requirement
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Requirements Table */}
      <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600', margin: '0 0 2px' }}>All Requirements</h3>
            <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>{total} requirements posted</p>
          </div>
          <button onClick={() => setActive('requirements')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' }}>
            <Plus size={13} /> Post Requirement
          </button>
        </div>
        {(requirements || []).length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Job Title', 'Budget', 'Experience', 'Skills', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: '#6b7280', fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(requirements || []).map(r => (
                <tr key={r._id} style={{ borderTop: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px 20px', color: '#111827', fontWeight: '500', fontSize: '13px' }}>{r.title}</td>
                  <td style={{ padding: '12px 20px', color: '#6b7280', fontSize: '13px' }}>₹{r.budget?.toLocaleString()}</td>
                  <td style={{ padding: '12px 20px', color: '#6b7280', fontSize: '13px' }}>{r.experience} yrs</td>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {(r.skills || []).slice(0, 2).map(s => (
                        <span key={s} style={{ padding: '2px 7px', background: '#f3f4f6', color: '#374151', borderRadius: '4px', fontSize: '10px', fontWeight: '500' }}>{s}</span>
                      ))}
                      {r.skills?.length > 2 && <span style={{ color: '#9ca3af', fontSize: '11px' }}>+{r.skills.length - 2}</span>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                      background: r.status === 'live' ? '#f0fdf4' : r.status === 'pending' ? '#fffbeb' : '#f9fafb',
                      color: r.status === 'live' ? '#16a34a' : r.status === 'pending' ? '#ca8a04' : '#6b7280' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: r.status === 'live' ? '#16a34a' : r.status === 'pending' ? '#ca8a04' : '#9ca3af' }}/>
                      {r.status?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    <button onClick={() => setActive('candidates')} style={{ padding: '5px 12px', background: 'white', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', fontFamily: 'inherit' }}>
                      View Candidates
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <FileText size={20} color="#9ca3af" />
            </div>
            <p style={{ color: '#374151', fontSize: '14px', fontWeight: '500', margin: '0 0 4px' }}>No requirements posted yet</p>
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 16px' }}>Post your first requirement to start finding talent</p>
            <button onClick={() => setActive('requirements')} style={{ padding: '8px 16px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }}>
              Post First Requirement
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
